import { useState, useMemo, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CanvasGroup } from '../types';

export interface CanvasGroupPickerProps {
  isOpen: boolean;
  onClose: () => void;
  groups: CanvasGroup[];
  onSelect: (groupId: string) => void;
  currentGroupIds?: Set<string>;
}

export function CanvasGroupPicker({ isOpen, onClose, groups, onSelect, currentGroupIds }: CanvasGroupPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const filteredGroups = useMemo(() => {
    const lower = search.toLowerCase();
    return groups.filter(g => !search || g.label.toLowerCase().includes(lower));
  }, [groups, search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" onClick={onClose}>
      <div
        className="bg-white rounded-lg border border-gray-200 shadow-xl w-80 max-h-[50vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-800">{t('canvasPicker.assignGroup')}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-gray-100">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('canvasPicker.searchGroup')}
              className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-300"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredGroups.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-gray-400">
              {search ? t('canvasPicker.noGroupsFound') : t('canvasPicker.noGroups')}
            </div>
          ) : (
            filteredGroups.map(group => {
              const isMember = currentGroupIds?.has(group.id);
              return (
                <button
                  key={group.id}
                  onClick={() => onSelect(group.id)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                >
                  <div
                    className="w-3 h-3 rounded flex-shrink-0 border"
                    style={{
                      backgroundColor: group.backgroundColor,
                      borderColor: isMember ? '#3b82f6' : '#e5e7eb',
                    }}
                  />
                  <span className="text-xs text-gray-700 truncate flex-1">{group.label}</span>
                  {isMember && <span className="text-[10px] text-blue-500 font-medium">{t('canvasPicker.member')}</span>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
